import posthog from 'posthog-js';

// Create a unique namespace for the widget's PostHog instance
const WIDGET_POSTHOG_KEY = '__alfinder_posthog__';

export const initializePosthog = (
  apiKey: string,
  host: string,
  sessionId: string,
) => {
  // Check if our widget-specific PostHog instance already exists
  if (!window[WIDGET_POSTHOG_KEY]) {
    // Check if there's already a global PostHog instance
    const hasGlobalPosthog = window.posthog !== undefined;

    // Check if the global PostHog instance belongs to our widget (backward compatibility)
    // We can identify our instance by checking if it uses our API host
    const isOurGlobalInstance =
      hasGlobalPosthog && window.posthog?.config?.api_host === host;

    if (isOurGlobalInstance) {
      // Migrate existing widget instance to isolated namespace
      console.log(
        '[Alfinder Widget] Migrating existing PostHog instance to isolated namespace',
      );
      window[WIDGET_POSTHOG_KEY] = window.posthog;

      // Add migration marker
      window[WIDGET_POSTHOG_KEY].register({
        migrated_from_global: true,
        migration_timestamp: Date.now(),
      });

      return window[WIDGET_POSTHOG_KEY];
    }

    if (hasGlobalPosthog && !isOurGlobalInstance) {
      console.log(
        '[Alfinder Widget] Detected existing PostHog instance. Creating isolated instance with enhanced separation.',
      );
    }

    // Create a new PostHog instance specifically for the widget with maximum isolation
    const widgetPosthog = posthog.init(
      apiKey,
      {
        api_host: host,
        persistence: 'localStorage',
        persistence_name: `__alfinder_posthog_${apiKey.slice(-8)}`, // Unique storage key
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: true,
        disable_persistence: false,
        // Enhanced isolation settings
        loaded: undefined,
        bootstrap: {}, // Empty bootstrap to prevent inheritance
        sanitize_properties: undefined,
        // Cookie isolation - prevents conflicts with other PostHog instances
        cookie_name: `__alfinder_ph_${apiKey.slice(-8)}`,
        cross_subdomain_cookie: false,
      },
      `alfinder_widget_${Date.now()}`, // Unique instance name with timestamp
    );

    if (sessionId && widgetPosthog) {
      console.log('[Alfinder Widget] posthog identify sessionId: ', sessionId);

      widgetPosthog.identify(sessionId);
      widgetPosthog.register({
        sessionId: sessionId,
        widget: 'alfinder',
        isolation_enabled: true,
        instance_id: `alfinder_widget_${Date.now()}`,
      });
    }

    // Store our widget-specific instance
    window[WIDGET_POSTHOG_KEY] = widgetPosthog;

    // NEVER set as global PostHog - always keep widget isolated
    // This prevents conflicts when other PostHog scripts load later
    console.log(
      '[Alfinder Widget] Keeping widget PostHog isolated - never setting as global to prevent conflicts',
    );
    console.log('[Alfinder Widget] Global PostHog exists:', hasGlobalPosthog);
    console.log('[Alfinder Widget] Widget will use isolated instance only');

    // Enhanced logging for debugging
    console.log('[Alfinder Widget] PostHog initialization complete:', {
      hasGlobalPosthog,
      widgetInstanceCreated: !!window[WIDGET_POSTHOG_KEY],
      widgetApiKey: `${apiKey.slice(0, 8)}...`,
      isolationMode: 'enhanced',
    });
  }
  return window[WIDGET_POSTHOG_KEY];
};

export const getPosthog = () => {
  // ALWAYS return only our widget's isolated instance - never fall back to global
  const widgetInstance = window[WIDGET_POSTHOG_KEY];

  if (!widgetInstance) {
    console.warn('[Alfinder Widget] No isolated PostHog instance found');
    return null;
  }

  // Additional safety check - ensure it's not a stub
  const captureString = widgetInstance.capture?.toString() || '';
  const isStub =
    captureString.includes('t.push([e]') ||
    captureString.includes('.push(') ||
    captureString.length < 50;

  if (isStub) {
    console.warn(
      '[Alfinder Widget] Widget PostHog instance is a stub - returning null',
    );
    return null;
  }

  return widgetInstance;
};

// Helper function to update PostHog identification when session changes
export const updatePosthogSession = (sessionId: string) => {
  const posthog = getPosthog();
  if (posthog && sessionId) {
    posthog.identify(sessionId);
    posthog.register({
      sessionId: sessionId,
    });
  }
};

export const setPersonPropertiesForFlags = () => {
  const posthog = getPosthog();

  // Early return if no PostHog instance or if it's not our widget's instance
  if (!posthog || !window[WIDGET_POSTHOG_KEY]) {
    console.log(
      '[Alfinder Widget] setPersonPropertiesForFlags - no widget PostHog instance',
    );
    return;
  }

  console.log('[Alfinder Widget] setPersonPropertiesForFlags');
  console.log(
    '[Alfinder Widget] window.alfinderDataLayer',
    window.alfinderDataLayer,
    posthog,
  );

  if (window.alfinderDataLayer && posthog) {
    console.log(
      '[Alfinder Widget] posthog.get_distinct_id() ',
      posthog.get_distinct_id(),
    );
    console.log(
      '[Alfinder Widget] posthog.get_session_id() ',
      posthog.get_session_id(),
    );

    // Use a more specific hook identifier for our widget
    const hookKey = '__alfinderDataLayerHooked_widget';

    // Check if we've already hooked into the dataLayer to avoid multiple hooks
    if (!window[hookKey]) {
      let originalPush = window.alfinderDataLayer.push;

      // Store reference to original push to prevent recursive calls
      const safePush = originalPush;

      window.alfinderDataLayer.push = function (...args) {
        // Use the stored reference to avoid infinite recursion
        const result = safePush.apply(this, args);

        const dataLayerEvent = args[0];
        if (dataLayerEvent && dataLayerEvent.event === 'customer::login') {
          // Only process if we have our widget's PostHog instance
          const widgetPosthog = window[WIDGET_POSTHOG_KEY];
          if (!widgetPosthog) return result;

          const customer = window?.alfinderDataLayer?.find(
            (event: any) => event.event === 'customer::login',
          )?.customer;

          console.log(
            '[Alfinder Widget] customer from alfinderDataLayer in posthog',
            customer,
          );

          try {
            if (customer && customer.email) {
              widgetPosthog.setPersonPropertiesForFlags({
                email: customer.email,
                storeUrl: window.location.href,
                widget: 'alfinder',
              });

              widgetPosthog.setPersonProperties({
                email: customer.email,
                storeUrl: window.location.href,
                widget: 'alfinder',
              });
            } else {
              widgetPosthog.setPersonPropertiesForFlags({
                email: null,
                storeUrl: window.location.href,
                widget: 'alfinder',
              });

              widgetPosthog.setPersonProperties({
                email: null,
                storeUrl: window.location.href,
                widget: 'alfinder',
              });
            }
          } catch (error) {
            console.error(
              '[Alfinder Widget] Error setting person properties:',
              error,
            );
          }
        }

        return result;
      };

      // Mark that we've hooked into the dataLayer with widget-specific key
      window[hookKey] = true;
    }
  }
};

// Recursion prevention flag
let isTrackingInProgress = false;

export const safeTrack = (
  eventName: string,
  properties?: Record<string, any>,
) => {
  // Prevent recursion by checking if we're already in a tracking call
  if (isTrackingInProgress && eventName !== 'order_completed') {
    console.warn(
      '[Alfinder Widget] Preventing recursive safeTrack call for:',
      eventName,
    );
    return;
  }

  try {
    isTrackingInProgress = true;

    // Use only our widget's PostHog instance to prevent recursion
    const posthog = window[WIDGET_POSTHOG_KEY];

    if (!posthog || typeof posthog.capture !== 'function') {
      console.warn(
        '[Alfinder Widget] PostHog instance not available for tracking',
      );
      return;
    }

    // Skip feature flag check to avoid potential recursion - just track the event
    // The alfinder_customer_user flag check was causing recursion issues

    // Add widget identifier to all events
    const enhancedProperties = {
      ...properties,
      widget: 'alfinder',
      widget_version: '1.0.0', // You might want to make this dynamic
    };

    // Direct capture call with timeout as additional safety
    setTimeout(() => {
      try {
        posthog.capture(eventName, enhancedProperties);
      } catch (captureError) {
        console.error(
          '[Alfinder Widget] Error in posthog.capture:',
          captureError,
        );
      }
    }, 0);
  } catch (error) {
    console.error('[Alfinder Widget] Error in safeTrack:', error);
  } finally {
    // Reset the flag after a short delay to allow the capture to complete
    setTimeout(() => {
      isTrackingInProgress = false;
    }, 10);
  }
};

/**
 * Check if a feature flag is enabled
 * Use this for simple on/off feature flags
 *
 * @example
 * // Simple boolean check
 * if (isFeatureEnabled('new-dashboard')) {
 *   // Show new dashboard
 * }
 *
 * @param flagKey - The key of the feature flag to check
 * @param defaultValue - Optional default value if the flag check fails
 * @returns boolean indicating if the feature flag is enabled
 */
export const isFeatureEnabled = (
  flagKey: string,
  defaultValue = false,
): boolean => {
  try {
    // Use only our widget's PostHog instance to prevent recursion
    const posthog = window[WIDGET_POSTHOG_KEY];

    if (!posthog || typeof posthog.isFeatureEnabled !== 'function') {
      console.warn(
        `[Alfinder Widget] PostHog instance not available for feature flag ${flagKey}`,
      );
      return defaultValue;
    }

    const result = posthog.isFeatureEnabled(flagKey);
    return result === true;
  } catch (error) {
    console.warn(
      `[Alfinder Widget] Failed to check feature flag ${flagKey}:`,
      error,
    );
    return defaultValue;
  }
};

/**
 * Get the value of a feature flag
 * Use this for flags that return any type of value (string, number, object, etc.)
 *
 * @example
 * // Boolean flag
 * const isNewDashboard = getFeatureFlag('new-dashboard', false);
 *
 * // String flag
 * const theme = getFeatureFlag('theme-color', 'light');
 *
 * // Number flag
 * const maxItems = getFeatureFlag('max-items', 10);
 *
 * // Object flag
 * const config = getFeatureFlag('dashboard-config', {
 *   layout: 'grid',
 *   itemsPerPage: 20
 * });
 *
 * @param flagKey - The key of the feature flag to get
 * @param defaultValue - Default value if the flag check fails
 * @returns The value of the feature flag or the default value
 */
export const getFeatureFlag = <T>(flagKey: string, defaultValue: T): T => {
  try {
    // Use only our widget's PostHog instance to prevent recursion
    const posthog = window[WIDGET_POSTHOG_KEY];

    if (!posthog || typeof posthog.getFeatureFlag !== 'function') {
      console.warn(
        `[Alfinder Widget] PostHog instance not available for feature flag ${flagKey}`,
      );
      return defaultValue;
    }

    const value = posthog.getFeatureFlag(flagKey);
    return (value as T) ?? defaultValue;
  } catch (error) {
    console.error(
      `[Alfinder Widget] Failed to get feature flag ${flagKey}:`,
      error,
    );
    return defaultValue;
  }
};
